import React from 'react';
import {
  Folder,
  Archive,
  Search,
  Plus,
  Sparkles,
  Coffee,
  Clock,
  FileText,
} from 'lucide-react';
import { Button } from '../Button';
import './EmptyState.css';

export type EmptyStateVariant =
  | 'first-project'
  | 'no-projects-in-stage'
  | 'no-archived'
  | 'no-search-results'
  | 'no-sessions'
  | 'no-notes'
  | 'generic';

export interface EmptyStateProps {
  variant?: EmptyStateVariant;
  title?: string;
  message?: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'ghost';
  };
  className?: string;
}

interface EmptyStateContent {
  icon: React.ReactNode;
  title: string;
  message: string;
}

const getDefaultContent = (variant: EmptyStateVariant): EmptyStateContent => {
  switch (variant) {
    case 'first-project':
      return {
        icon: <Sparkles size={56} />,
        title: 'Welcome to your creative space',
        message: 'Every journey begins with a single step. Start your first project whenever you feel ready - there\'s no rush.',
      };

    case 'no-projects-in-stage':
      return {
        icon: <Coffee size={40} />,
        title: 'This stage is waiting',
        message: 'Drag a project here or create a new one when inspiration strikes.',
      };

    case 'no-archived':
      return {
        icon: <Archive size={48} />,
        title: 'No archived projects yet',
        message: 'Completed projects will appear here when you archive them. Take your time with your current work.',
      };

    case 'no-search-results':
      return {
        icon: <Search size={48} />,
        title: 'No projects found',
        message: 'Try adjusting your search or filters. Your projects are still here, just hidden from view.',
      };

    case 'no-sessions':
      return {
        icon: <Clock size={48} />,
        title: 'No work sessions yet',
        message: 'Start a session when you\'re ready to work. Each session is a step forward in your creative journey.',
      };

    case 'no-notes':
      return {
        icon: <FileText size={48} />,
        title: 'No notes yet',
        message: 'Document your progress, ideas, and milestones as you work on this project.',
      };

    case 'generic':
    default:
      return {
        icon: <Folder size={48} />,
        title: 'Nothing here yet',
        message: 'This space is ready for your creative work.',
      };
  }
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  variant = 'generic',
  title,
  message,
  icon,
  action,
  className = '',
}) => {
  const defaultContent = getDefaultContent(variant);

  return (
    <div className={`empty-state empty-state--${variant} ${className}`}>
      <div className="empty-state__icon">
        {icon || defaultContent.icon}
      </div>
      <h3 className="empty-state__title">
        {title || defaultContent.title}
      </h3>
      <p className="empty-state__message">
        {message || defaultContent.message}
      </p>
      {action && (
        <div className="empty-state__action">
          <Button
            variant={action.variant || 'primary'}
            onClick={action.onClick}
          >
            {variant === 'first-project' && <Plus size={16} />}
            {action.label}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmptyState;
